import { useState, useMemo, useCallback, useEffect } from "react";
import { Box, FormControl, FormHelperText, FormLabel } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useSnackbar } from "notistack";
import { uploadClaimAttachment } from "./useClaimAttachmentFiles.js";
import { getAttachmentLabel } from "../../../claim/costs/costFieldSchemas.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useAttachmentConfig } from "./useAttachmentConfig.js";
import { useClaimCostTypes } from "../../../../hooks/useClaimCostTypes.js";
import FilePickerDialog from "./FilePickerDialog.jsx";
import DocumentViewer from "../../../MDDForm/Viewer/DocumentViewer.jsx";
import EditButton from "@accredo/fe-shared/theme/components/EditButton.jsx";
import AttachmentFileRow from "./AttachmentFileRow.jsx";

/**
 * File picker for general claim attachments with cost awareness.
 *
 * Files already linked to cost entries or other form attachment fields are shown
 * as "unselectable" with a badge indicating which field/cost they're used in.
 * This prevents accidental duplicate uploads while keeping users informed.
 *
 * Props (FormRenderer integration):
 *   name                       — field name (for form tracking)
 *   label                      — field label
 *   multiple                   — boolean (default true)
 *   claimId                    — number|string|null  current claim ID (null for new claims)
 *   claimAttachmentFiles       — array  existing files fetched by FormRenderer (avoids duplicate fetch)
 *   costs                      — array  current costs from form state
 *   attachmentSources          — array  other form attachment fields with {name, label, value}
 *   addDescription             — boolean  allow editing file descriptions
 *   value                      — current value (array of IDs from FormRenderer)
 *   onChange                   — callback(value) when selection changes
 *   onBlur                     — callback() when field is blurred
 *   disabled                   — boolean
 *   error                      — error message string
 *   helperText                 — static helper text
 *   sessionAttachmentFiles     — array of session files from FormRenderer
 *   addSessionAttachmentFile   — callback(file, uploadFn) from FormRenderer
 *   updateSessionAttachmentFileDescription — callback(uuid, desc) from FormRenderer
 *   showUnselectableAsSelected — boolean  show unselectable files as selected (default: true for claim attachments)
 *   showOtherFieldFiles        — boolean  show files assigned to other fields in the list (default: false)
 *   preventSelectingOtherFieldFiles — boolean  prevent selecting files already used in other fields (default: false)
 */

const enrichWithUsedIn = (files, costAttachments, formAttachments) =>
  files.map((file) => {
    const fileId = file.id != null ? Number(file.id) : null;
    const usedIn = [];

    for (const ref of costAttachments.filter((ca) => ca.serverId === fileId)) {
      usedIn.push({ source: "cost", label: ref.costLabel });
    }
    for (const ref of formAttachments.filter((fa) => fa.serverId === fileId)) {
      usedIn.push({
        source: "form",
        label: ref.sourceLabel,
      });
    }

    return { ...file, usedIn: usedIn.length > 0 ? usedIn : null };
  });

export default function ClaimGeneralAttachmentsField({
  name,
  label,
  multiple = true,
  claimId,
  claimAttachmentFiles = [],
  costs = [],
  attachmentSources = [],
  addDescription = false,
  trackAsSource = false,
  value,
  onChange,
  onBlur,
  disabled = false,
  error,
  helperText,
  sessionAttachmentFiles = [],
  addSessionAttachmentFile,
  updateSessionAttachmentFileDescription,
  showOtherFieldFiles = false,
  preventSelectingOtherFieldFiles = false,
}) {
  const configExtensions = useAttachmentConfig();
  const { config: costTypeConfig } = useClaimCostTypes();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documentToView, setDocumentToView] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const sessionFiles = sessionAttachmentFiles;

  // Collect cost-related attachment references with metadata
  const costAttachments = useMemo(() => {
    const attachments = [];
    for (const cost of costs) {
      if (!cost?.data) continue;

      for (const [key, value] of Object.entries(cost.data)) {
        if (!key.endsWith("_attachment") || !value) continue;

        const serverId = typeof value === "number" ? value : value?.id;
        const normalizedServerId = serverId != null ? Number(serverId) : null;
        if (!normalizedServerId) continue;

        attachments.push({
          serverId: normalizedServerId,
          costId: cost.id,
          costLabel: getAttachmentLabel(costTypeConfig, cost.cost_type, key),
          fieldName: key,
          source: "cost",
        });
      }
    }
    return attachments;
  }, [costs, costTypeConfig]);

  // Collect form attachment field references with metadata
  const formAttachments = useMemo(() => {
    const attachments = [];
    for (const source of attachmentSources) {
      if (!source.value) continue;

      // Normalize value to array of attachment refs
      let attachmentRefs = [];
      if (Array.isArray(source.value)) {
        attachmentRefs = source.value;
      } else if (typeof source.value === "object" && source.value !== null) {
        // Wrapped format: { __filepicker__: "single"|"multi", attachments: {...}|[...] }
        if (source.value.__filepicker__) {
          const inner = source.value.attachments;
          if (Array.isArray(inner)) {
            attachmentRefs = inner;
          } else if (inner != null) {
            attachmentRefs = [inner];
          }
        } else if (source.value.id) {
          // Bare single attachment object (storage format)
          attachmentRefs = [source.value];
        }
      }

      for (const ref of attachmentRefs) {
        const serverId = typeof ref === "number" ? ref : ref?.id;
        const normalizedServerId = serverId != null ? Number(serverId) : null;
        if (!normalizedServerId) continue;

        attachments.push({
          serverId: normalizedServerId,
          sourceLabel: source.label,
          fieldName: source.name,
          source: "form",
        });
      }
    }
    return attachments;
  }, [attachmentSources]);

  const unselectableIds = useMemo(() => {
    if (!preventSelectingOtherFieldFiles) {
      return []; // files can be linked to multiple fields by default
    }
    const costIds = costAttachments.map((ca) => ca.serverId);
    const formIds = formAttachments.map((fa) => fa.serverId);
    return [...costIds, ...formIds].filter(Boolean);
  }, [costAttachments, formAttachments, preventSelectingOtherFieldFiles]);

  const existingFilesWithUsedIn = useMemo(
    () =>
      enrichWithUsedIn(
        claimAttachmentFiles || [],
        costAttachments,
        formAttachments
      ),
    [claimAttachmentFiles, costAttachments, formAttachments]
  );

  const sessionFilesWithUsedIn = useMemo(
    () => enrichWithUsedIn(sessionFiles, costAttachments, formAttachments),
    [sessionFiles, costAttachments, formAttachments]
  );

  // Bind upload to the current claimId (null → orphan attachment for new claims)
  const uploadFn = useCallback(
    (file, uuid, description) =>
      uploadClaimAttachment(claimId, file, uuid, description),
    [claimId]
  );

  // Normalize value - handles:
  // - Wrapped format: { __filepicker__: "single"|"multi", attachments: {id,uuid,...}|[...] }
  // - Storage format: {id}|[{id: 123}, {id: 456}]
  const rawAttachments = useMemo(() => {
    if (value === null || value === undefined) return [];
    // Wrapped format from filepicker (fresh submission)
    if (typeof value === "object" && value.__filepicker__) {
      const attachments = value.attachments;
      if (Array.isArray(attachments)) return attachments;
      // single mode: attachments is a single object
      return attachments != null ? [attachments] : [];
    }
    // storage format: array of objects with id or object for single mode
    if (multiple && Array.isArray(value)) {
      return value;
    } else if (!multiple && typeof value === "object") {
      return [value];
    }
    return [];
  }, [value]);

  const selectedAttachmentRefs = multiple
    ? rawAttachments
    : rawAttachments.slice(0, 1);

  // extract plain ids for FilePickerDialog
  const selectedIds = selectedAttachmentRefs
    .map((ref) => ref?.id)
    .filter(Boolean);

  const allFiles = [...existingFilesWithUsedIn, ...sessionFilesWithUsedIn];

  const resolveFiles = (attachmentRefs) =>
    (attachmentRefs || [])
      .map((ref) => {
        const normalizedId = ref?.id != null ? Number(ref.id) : null;
        return allFiles.find(
          (f) => (f.id != null ? Number(f.id) : null) === normalizedId
        );
      })
      .filter(Boolean);

  const selectedFiles = resolveFiles(selectedAttachmentRefs);

  // Files that are used in other fields/costs (read-only, only shown when showOtherFieldFiles is true)
  const otherFieldFiles = useMemo(() => {
    if (!showOtherFieldFiles) {
      return [];
    }
    return allFiles.filter((f) => {
      const fileId = f.id != null ? Number(f.id) : null;
      return (
        f.usedIn &&
        !selectedAttachmentRefs.some(
          (ref) => (ref?.id != null ? Number(ref.id) : null) === fileId
        )
      );
    });
  }, [allFiles, selectedAttachmentRefs, showOtherFieldFiles]);

  const displayedFiles = useMemo(() => {
    return [...selectedFiles, ...otherFieldFiles];
  }, [selectedFiles, otherFieldFiles]);

  const buildAttachmentRefs = (files) => {
    const refs = files.map((f) => {
      // When this field is a tracked source, its own label is always the primary
      // used_in tag. Cross-references from other fields (f.usedIn) are appended.
      // Falls back to the backend-persisted value when no form-state info exists.
      const formDerivedTags = [
        ...(trackAsSource && label ? [label] : []),
        ...(f.usedIn ? f.usedIn.map((u) => u.label) : []),
      ];
      return {
        id: f.id,
        uuid: f.uuid ?? null,
        used_in:
          formDerivedTags.length > 0 ? formDerivedTags : (f.used_in ?? []),
        ...(f.description != null ? { description: f.description } : {}),
      };
    });
    return multiple ? refs : (refs[0] ?? null);
  };

  const buildValue = (attachmentRefs) => {
    if (attachmentRefs === null) return null;
    const isEmpty = Array.isArray(attachmentRefs)
      ? attachmentRefs.length === 0
      : false;
    if (isEmpty) return null;
    return {
      __filepicker__: multiple ? "multi" : "single",
      attachments: attachmentRefs,
    };
  };

  // Whenever cost/field attribution changes, push fresh used_in labels back into
  // the form value so that re-saving without reopening the picker still submits
  // the correct attribution. Keyed on content rather than array identity:
  // callers rebuild costs/attachmentSources on every render, and onChange
  // re-renders them, so an identity dep would loop. selectedAttachmentRefs is
  // excluded from deps for the same reason.
  const attributionKey = JSON.stringify([
    costAttachments.map((ca) => [ca.serverId, ca.costLabel]),
    formAttachments.map((fa) => [fa.serverId, fa.sourceLabel]),
  ]);
  useEffect(() => {
    if (!selectedAttachmentRefs.length) return;
    const files = resolveFiles(selectedAttachmentRefs);
    if (!files.length) return;
    onChange?.(buildValue(buildAttachmentRefs(files)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributionKey]);

  const handleConfirm = (newIds) => {
    const files = resolveFiles(newIds.map((id) => ({ id })));
    onChange?.(buildValue(buildAttachmentRefs(files)));
    setDialogOpen(false);
  };

  const handleRemove = (id) => {
    const next = selectedAttachmentRefs.filter(
      (ref) => Number(ref?.id) !== Number(id)
    );
    const remainingFiles = resolveFiles(next);
    onChange?.(buildValue(buildAttachmentRefs(remainingFiles)));
  };

  const handlePreview = async (file) => {
    const result = await backendGet(
      `data/anlages/${file.id}?include[]=filedata`,
      {
        label: "Datei laden",
        setIsLoading: () => {},
        setResult: () => {},
        enqueueSnackbar,
      }
    );

    if (result) {
      const doc = {
        success: true,
        id: result.id,
        filename: result.filename,
        filesize: result.filesize,
        filetype: result.filetype,
        mimetype: result.mimetype,
        filedata: result.filedata,
        label: result.bezeichnung || result.filename,
      };
      setDocumentToView(null);
      setTimeout(() => setDocumentToView(doc), 0);
    } else {
      enqueueSnackbar("Fehler beim Laden der Datei", { variant: "error" });
    }
  };

  const hasError = Boolean(error);

  return (
    <FormControl error={hasError} fullWidth disabled={disabled}>
      {label && (
        <FormLabel
          sx={{
            mb: 1,
            typography: "body2",
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {label}
        </FormLabel>
      )}

      <Box sx={{ mt: "8px" }}>
        {/* Selected and cost-linked file rows */}
        <Box mb={2}>
          {displayedFiles.map((f) => {
            const hasOtherSources =
              Array.isArray(f.usedIn) && f.usedIn.length > 0;
            return (
              <AttachmentFileRow
                key={f.id}
                file={f}
                usedIn={f.usedIn}
                disabled={hasOtherSources}
                onPreview={disabled ? null : () => handlePreview(f)}
                onRemove={
                  disabled || hasOtherSources ? null : () => handleRemove(f.id)
                }
              />
            );
          })}
        </Box>

        {/* Trigger button */}
        <EditButton
          size="small"
          variant="outlined"
          color={hasError ? "error" : "primary"}
          startIcon={<AttachFileIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={disabled}
          onBlur={onBlur}
        >
          {selectedFiles.length > 0
            ? multiple
              ? "Auswahl bearbeiten"
              : "Bearbeiten"
            : multiple
              ? "Dateien auswählen"
              : "Datei auswählen"}
        </EditButton>
      </Box>

      {/* helper text or error message */}
      {(hasError || helperText) && (
        <FormHelperText>{hasError ? error : helperText}</FormHelperText>
      )}

      <FilePickerDialog
        open={dialogOpen}
        onClose={() => {
          onBlur?.();
          setDialogOpen(false);
        }}
        onConfirm={handleConfirm}
        existingFiles={existingFilesWithUsedIn}
        sessionFiles={sessionFilesWithUsedIn}
        onAddSessionFile={(file) => addSessionAttachmentFile(file, uploadFn)}
        onUpdateSessionFileDescription={updateSessionAttachmentFileDescription}
        initialSelectedIds={selectedIds}
        multiple={multiple}
        title={label}
        accept={configExtensions ?? []}
        addDescription={addDescription}
        unselectableIds={unselectableIds}
        showUnselectableAsSelected={true}
      />
      <DocumentViewer key={documentToView?.id} document={documentToView} />
    </FormControl>
  );
}
